import { Auth } from "aws-amplify";

/**
 * isSafeLoginRedirect validates that REACT_APP_LOGIN_ROUTE is a SAME-ORIGIN
 * relative path before we navigate. The original code did
 * `window.location.href = process.env.REACT_APP_LOGIN_ROUTE` unconditionally
 * — if that env var were ever empty, undefined, or set to an external URL
 * (build-time misconfiguration, an attacker controlling the build pipeline,
 * or a developer pasting a wrong value), every Cognito-session failure
 * became an open-redirect that could phish a fresh login.
 *
 * Acceptance rule: the route must start with "/" and NOT with "//" (the
 * latter is a protocol-relative URL that browsers treat as cross-origin).
 */
function isSafeLoginRedirect(route: string | undefined | null): route is string {
  if (typeof route !== "string" || route.length === 0) return false;
  if (!route.startsWith("/")) return false;
  if (route.startsWith("//")) return false;
  return true;
}

const SAFE_FALLBACK_LOGIN_ROUTE = "/login";

/**
 * The configured login route, falling back to the default when unset or unsafe.
 */
function safeLoginRoute(): string {
  const configured = process.env.REACT_APP_LOGIN_ROUTE;
  return isSafeLoginRedirect(configured) ? configured : SAFE_FALLBACK_LOGIN_ROUTE;
}

/**
 * Send the browser to the login page, leaving the single-page app entirely.
 *
 * MUST be a full-page navigation, never react-router's `navigate()`. The
 * authenticated half of the app is mounted as
 * `<BrowserRouter basename={"/" + variant}><PrivateRouter/></BrowserRouter>`
 * (see `routers/RootRouter.tsx`), and `PrivateRouter` declares no `/login`
 * route — login lives in `PublicRouter`, which `RootRouter` mounts only when
 * the INITIAL pathname is public. A client-side navigation to `/login` from
 * inside the private tree therefore resolves against the private route table,
 * matches nothing, and lands on `<Route path="*" element={<NotFoundPage/>}/>`.
 * The user sees the app's own 404 at `/v{N}/login` instead of a login form —
 * which is exactly how a role that could not read `/api/users/me` was reported
 * as "404 page not found" rather than as a permission problem.
 *
 * Exported so both auth bounce sites share one implementation; they previously
 * differed (`window.location.href` in one, `navigate()` in the other) and only
 * one of them worked.
 */
function redirectToLogin(): void {
  window.location.assign(safeLoginRoute());
}

// Module-level dedup. When N components mount simultaneously (e.g. the
// monitoring grid spawning 11 MonitoringBank cards at once) they would
// previously each fire an independent /offline/refresh request, tripping the
// server's rate limiter (429) for the late arrivals. Worse, the OFFLINE
// branch's promise had two paths to silent hangs: (a) the response JSON
// arrived without a `.token` field — neither resolve nor reject was called;
// (b) the rate-limit response surfaced through `.then` (not `.catch`) and hit
// the same dead end. Hung promises left their owning components with
// `token === null` forever, `skip: !token` kept the bank query off, and
// MonitoringBank rendered BLANK_BANK with only a HeartBeat dot.
let inflightToken: Promise<string> | null = null;

/**
 * localStorage key of the offline ACCESS token: the bearer every API call and
 * the monitoring WebSocket present. /offline/refresh returns a 15-minute one.
 */
const OFFLINE_TOKEN_KEY = "offlineToken";

/**
 * localStorage key of the offline REFRESH token (`refresh_token` in the
 * /offline/refresh response). It is only ever presented to /offline/refresh,
 * never to an API: the server does not accept it as an API bearer.
 *
 * Presenting it, rather than the access token, is what tells the server this
 * client keeps refresh tokens (LIFT-1085). Without one the server has to let
 * an expired access token be refreshed as a compatibility grace for older
 * frontends; with one it does not.
 */
const OFFLINE_REFRESH_TOKEN_KEY = "offlineRefreshToken";

/**
 * Store a token that STARTS a new offline session state — the /offline/login
 * token or a step-up token — as the current bearer, and drop any refresh
 * token.
 *
 * Dropping it matters for step-up: a refresh token carries the step-up time of
 * the token it was minted from. Presenting the older refresh token after a
 * step-up would hand back an access token WITHOUT the fresh step-up, silently
 * undoing it on the very next request. With no refresh token stored, the next
 * refresh presents this token instead and receives a refresh token that
 * carries it. /offline/login returns no refresh token for the same reason:
 * the chain starts at the first refresh.
 */
function storeOfflineSessionToken(token: string): void {
  localStorage.setItem(OFFLINE_TOKEN_KEY, token);
  localStorage.removeItem(OFFLINE_REFRESH_TOKEN_KEY);
}

/**
 * Forget the offline session: the access token and the refresh token. Every
 * logout path must call this, or a refresh token outlives the session in the
 * browser for up to 30 days.
 */
function clearOfflineSession(): void {
  try {
    localStorage.removeItem(OFFLINE_TOKEN_KEY);
    localStorage.removeItem(OFFLINE_REFRESH_TOKEN_KEY);
  } catch {
    // localStorage may be unavailable (private mode, some test environments).
  }
}

type OfflineRefreshResponse = { token?: string; refresh_token?: string };

const nonEmptyString = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0;

const refreshOfflineToken = (): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    // `cached` is the access token: the only thing ever handed back as a
    // bearer, including by the fallbacks below.
    const cached = localStorage.getItem(OFFLINE_TOKEN_KEY);
    // Present the refresh token when there is one. Right after /offline/login
    // or a step-up there is none, and the current token is presented instead.
    const refreshCredential = localStorage.getItem(OFFLINE_REFRESH_TOKEN_KEY) || cached;
    fetch(`${process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE}/offline/refresh`, {
      method: "POST",
      headers: new Headers({
        Authorization: `Bearer ${refreshCredential ?? ""}`,
      }),
    })
      .then(async (response) => {
        let json: OfflineRefreshResponse | null = null;
        try {
          json = (await response.json()) as OfflineRefreshResponse;
        } catch {
          json = null;
        }
        if (response.ok && json && nonEmptyString(json.token)) {
          localStorage.setItem(OFFLINE_TOKEN_KEY, json.token);
          if (nonEmptyString(json.refresh_token)) {
            localStorage.setItem(OFFLINE_REFRESH_TOKEN_KEY, json.refresh_token);
          }
          resolve(`Bearer ${json.token}`);
          return;
        }
        // Refresh failed (rate limit, server error, malformed body). Fall
        // back to the cached token so existing subscriptions keep working
        // instead of hanging forever. If there is no cached token at all,
        // reject loudly so callers can surface a real auth failure.
        if (cached && cached.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            `[GETJwtToken] /offline/refresh failed (status=${response.status}) — using cached offlineToken so dependent queries can proceed.`,
          );
          resolve(`Bearer ${cached}`);
          return;
        }
        reject(
          new Error(
            `[GETJwtToken] /offline/refresh returned status ${response.status} with no token in body and no cached fallback.`,
          ),
        );
      })
      .catch((err) => {
        if (cached && cached.length > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            `[GETJwtToken] /offline/refresh network error (${String(
              err,
            )}) — using cached offlineToken.`,
          );
          resolve(`Bearer ${cached}`);
          return;
        }
        reject(err instanceof Error ? err : new Error(String(err)));
      });
  });

/**
 * Resolves the current JWT, refreshing it when needed.
 *
 * Concurrent callers share one in-flight request rather than each triggering a
 * refresh, which is what `inflightToken` is for.
 */
const GETJwtToken = (): Promise<string> => {
  if (inflightToken) return inflightToken;
  let promise: Promise<string>;
  if (process.env.REACT_APP_ENVIRONMENT !== "OFFLINE") {
    // Local-connected demo escape hatch: in a DEVELOPMENT build only, a manually
    // injected offline JWT lets the DEVELOPMENT/EDGE build authenticate REST
    // calls against a SERVER_TYPE=edge cloud (local JWT) without Cognito.
    // Allow-listed to DEVELOPMENT so it is fail-closed — compiled out (dead
    // code) in production/staging/any other build, where Cognito remains the
    // only auth path.
    const injected = localStorage.getItem(OFFLINE_TOKEN_KEY);
    if (injected && process.env.REACT_APP_ENVIRONMENT === "DEVELOPMENT") {
      promise = Promise.resolve(`Bearer ${injected}`);
    } else {
      promise = new Promise<string>((resolve, reject) => {
        Auth.currentSession()
          .then((data) => {
            const jwt = data.getIdToken();
            resolve(`Bearer ${jwt.getJwtToken()}`);
          })
          .catch((err) => {
            Auth.signOut();
            redirectToLogin();
            reject(err);
          });
      });
    }
  } else {
    promise = refreshOfflineToken();
  }
  inflightToken = promise.finally(() => {
    inflightToken = null;
  });
  return inflightToken;
};

export default GETJwtToken;
export {
  isSafeLoginRedirect,
  safeLoginRoute,
  redirectToLogin,
  SAFE_FALLBACK_LOGIN_ROUTE,
  OFFLINE_TOKEN_KEY,
  OFFLINE_REFRESH_TOKEN_KEY,
  storeOfflineSessionToken,
  clearOfflineSession,
};
